import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  FormField,
  ActionButton,
} from '@shared/components/add-form/add-form.component';

// ============================================================================
// INTERFACES
// ============================================================================

export interface StockItem {
  id?: string;
  brandId: string;
  brandName: string;
  categoryName: string;
  lockNum: number;
  preemptionNum: number;
  reservedNum: number;
  skuCode: string;
  skuName: string;
  spuCode: string;
  spuName: string;
  spuType: number;
  stockNum: number;
  typeCode: string;
  usableNum: number;
  waitNum: number;
  warehouseCode: string;
  warehouseId: string;
  warehouseName: string;
  wayNum: number;
  /** HELD ("Đã giữ"): reservedNum + preemptionNum + lockNum, sourced from IMS, not raw fields. */
  heldNum: number;
  /** DEFECTIVE ("Hàng lỗi"): damaged quantity, sourced from IMS. */
  defectiveNum: number;
  /** ON_ORDER ("Đặt hàng"): quantity ordered from supplier, not yet received, sourced from IMS. */
  onOrderNum: number;
  exceedZero?: boolean;
}

export interface StockListResponse {
  data: StockItem[];
  totalRecords: number;
  loading: boolean;
}

export interface StockFormConfig {
  title: string;
  formFields: FormField[];
  headerActions: ActionButton[];
  listRoute: string;
}

// ============================================================================
// SEARCH FORM CONFIGURATIONS
// ============================================================================

export const STOCK_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'spuCode',
    label: 'inventory_lookup.search.fields.product_code',
    type: 'text',
    placeholder: 'inventory_lookup.search.fields.product_code',
  },
  {
    key: 'spuName',
    label: 'inventory_lookup.search.fields.product_name',
    type: 'text',
    placeholder: 'inventory_lookup.search.fields.product_name',
  },
  {
    key: 'warehouseCode',
    label: 'inventory_lookup.search.fields.warehouse_code',
    type: 'select',
    placeholder: 'inventory_lookup.search.fields.warehouse_code',
    options: [], // Will be populated from API
    showClear: true,
  },
  // {
  //   key: 'warehouseId',
  //   label: 'Warehouse Name',
  //   type: 'text',
  //   placeholder: 'Enter warehouse name'
  // },
  {
    key: 'skuCode',
    label: 'inventory_lookup.search.fields.sku_code',
    type: 'text',
    placeholder: 'inventory_lookup.search.fields.sku_code',
  },
  {
    key: 'categoryIdList',
    label: 'inventory_lookup.search.fields.product_category',
    type: 'multiselect',
    placeholder: 'inventory_lookup.search.fields.product_category',
    options: [], // Will be populated from API
  },
  // {
  //   key: 'exceedZero',
  //   label: 'Stock Greater Than Zero',
  //   type: 'select',
  //   options: STOCK_STATUS_OPTIONS,
  //   placeholder: 'Select stock status'
  // }
];

// ============================================================================
// TABLE COLUMN CONFIGURATIONS
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const STOCK_SEARCH_DEFAULT_COLUMNS = {
  SPU_CODE: 'spuCode',
  SPU_NAME: 'spuName',
  SKU_CODE: 'skuCode',
  WAREHOUSE_CODE: 'warehouseCode',
  WAREHOUSE_NAME: 'warehouseName',
  // The 5 BRD inventory statuses (RBA-2314/RBA-2315) - all sourced from IMS, none self-calculated.
  USABLE_NUM: 'usableNum',
  HELD_NUM: 'heldNum',
  IN_TRANSIT_NUM: 'wayNum',
  DEFECTIVE_NUM: 'defectiveNum',
  ON_ORDER_NUM: 'onOrderNum',
  BRAND_NAME: 'brandName',
  CATEGORY_NAME: 'categoryName',
} as const;

/**
 * Flexible column property names (can be shown/hidden via column selector)
 * Update these constants to change flexible column property names
 */
export const STOCK_SEARCH_FLEXIBLE_COLUMNS = {
  // 4 of the 5 BRD statuses - unchecked by default, user can opt in via column selector
  HELD_NUM: 'heldNum',
  IN_TRANSIT_NUM: 'wayNum',
  DEFECTIVE_NUM: 'defectiveNum',
  ON_ORDER_NUM: 'onOrderNum',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const STOCK_SEARCH_DEFAULT_COLUMNS_ORDER: string[] = [
  STOCK_SEARCH_DEFAULT_COLUMNS.SPU_CODE,
  STOCK_SEARCH_DEFAULT_COLUMNS.SPU_NAME,
  STOCK_SEARCH_DEFAULT_COLUMNS.SKU_CODE,
  STOCK_SEARCH_DEFAULT_COLUMNS.WAREHOUSE_CODE,
  STOCK_SEARCH_DEFAULT_COLUMNS.WAREHOUSE_NAME,
  STOCK_SEARCH_DEFAULT_COLUMNS.USABLE_NUM,
  STOCK_SEARCH_DEFAULT_COLUMNS.BRAND_NAME,
  STOCK_SEARCH_DEFAULT_COLUMNS.CATEGORY_NAME,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const STOCK_SEARCH_FLEXIBLE_COLUMNS_ORDER: string[] = [
  STOCK_SEARCH_FLEXIBLE_COLUMNS.HELD_NUM,
  STOCK_SEARCH_FLEXIBLE_COLUMNS.IN_TRANSIT_NUM,
  STOCK_SEARCH_FLEXIBLE_COLUMNS.DEFECTIVE_NUM,
  STOCK_SEARCH_FLEXIBLE_COLUMNS.ON_ORDER_NUM,
];

/**
 * Full table column order (default + flexible interleaved to match STOCK_TABLE_COLUMNS grouping,
 * so toggling a flexible column on inserts it next to the other stock-quantity columns)
 */
export const STOCK_SEARCH_REFERENCE_COLUMNS_ORDER: string[] = [
  STOCK_SEARCH_DEFAULT_COLUMNS.SPU_CODE,
  STOCK_SEARCH_DEFAULT_COLUMNS.SPU_NAME,
  STOCK_SEARCH_DEFAULT_COLUMNS.SKU_CODE,
  STOCK_SEARCH_DEFAULT_COLUMNS.WAREHOUSE_CODE,
  STOCK_SEARCH_DEFAULT_COLUMNS.WAREHOUSE_NAME,
  STOCK_SEARCH_DEFAULT_COLUMNS.USABLE_NUM,
  STOCK_SEARCH_FLEXIBLE_COLUMNS.HELD_NUM,
  STOCK_SEARCH_FLEXIBLE_COLUMNS.IN_TRANSIT_NUM,
  STOCK_SEARCH_FLEXIBLE_COLUMNS.DEFECTIVE_NUM,
  STOCK_SEARCH_FLEXIBLE_COLUMNS.ON_ORDER_NUM,
  STOCK_SEARCH_DEFAULT_COLUMNS.BRAND_NAME,
  STOCK_SEARCH_DEFAULT_COLUMNS.CATEGORY_NAME,
];

export const STOCK_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'inventory_lookup.table.columns.product_code',
    prop: STOCK_SEARCH_DEFAULT_COLUMNS.SPU_CODE,
    width: 120,
  },
  {
    label: 'inventory_lookup.table.columns.product_name',
    prop: STOCK_SEARCH_DEFAULT_COLUMNS.SPU_NAME,
    width: 140,
  },
  {
    label: 'inventory_lookup.table.columns.sku_code',
    prop: STOCK_SEARCH_DEFAULT_COLUMNS.SKU_CODE,
    width: 120,
  },
  {
    label: 'inventory_lookup.table.columns.warehouse_code',
    prop: STOCK_SEARCH_DEFAULT_COLUMNS.WAREHOUSE_CODE,
    width: 140,
  },
  {
    label: 'inventory_lookup.table.columns.warehouse_name',
    prop: STOCK_SEARCH_DEFAULT_COLUMNS.WAREHOUSE_NAME,
    width: 160,
  },
  {
    label: 'inventory_lookup.table.columns.available',
    prop: STOCK_SEARCH_DEFAULT_COLUMNS.USABLE_NUM,
    width: 120,
    headerTooltip: 'inventory_lookup.table.columns.available_tooltip',
  },
  {
    label: 'inventory_lookup.table.columns.held',
    prop: STOCK_SEARCH_DEFAULT_COLUMNS.HELD_NUM,
    width: 120,
    headerTooltip: 'inventory_lookup.table.columns.held_tooltip',
  },
  {
    label: 'inventory_lookup.table.columns.in_transit',
    prop: STOCK_SEARCH_DEFAULT_COLUMNS.IN_TRANSIT_NUM,
    width: 120,
  },
  {
    label: 'inventory_lookup.table.columns.defective',
    prop: STOCK_SEARCH_DEFAULT_COLUMNS.DEFECTIVE_NUM,
    width: 120,
    headerTooltip: 'inventory_lookup.table.columns.defective_tooltip',
  },
  {
    label: 'inventory_lookup.table.columns.on_order',
    prop: STOCK_SEARCH_DEFAULT_COLUMNS.ON_ORDER_NUM,
    width: 120,
    headerTooltip: 'inventory_lookup.table.columns.on_order_tooltip',
  },
  {
    label: 'inventory_lookup.table.columns.brand_name',
    prop: STOCK_SEARCH_DEFAULT_COLUMNS.BRAND_NAME,
    width: 140,
  },
  {
    label: 'inventory_lookup.table.columns.product_categories',
    prop: STOCK_SEARCH_DEFAULT_COLUMNS.CATEGORY_NAME,
    width: 160,
  },
];
